<template>
  <base-modal
    ref="baseModalRef"
    :title="`Calculation breakdown: ${props.customField.name}`"
    :primary-color="Colors.BLUE"
    :show-row2="false"
    :show-footer="true"
    :primary-action-text="withShortcut('Done', shortcuts.SAVE)"
    :secondary-action-text="withShortcut('Cancel', shortcuts.CANCEL)"
    :auto-focus-primary-action="true"
    @save="closePopup"
    @cancel="closePopup"
    @hide="onHide"
  >
    <div class="tw-p-3">
      <div v-if="props.customField.description" class="tw-mb-1">
        <label class="input-label">Description</label>
        <div class="tw-text-sm">
          {{ props.customField.description }}
        </div>
      </div>
      <div class="tw-flex tw-items-end">
        <label class="input-label">Formula</label>
        <button
          class="primary-link input-label tw-ml-2 tw-font-semibold"
          @click="showProcessed = !showProcessed"
        >
          {{ showProcessed ? 'Show raw' : 'Show processed' }}
        </button>
        <div v-if="isAdmin" class="tw-ml-auto">
          <router-link
            :to="{
              name: RouteNames.OrganizationSettings,
              params: {
                organization: organization?.name,
              },
              query: {
                section: 'customFields',
                editCustomFieldId: props.customField.id,
              },
            }"
            class="primary-link tw-text-xs tw-font-semibold"
          >
            Manage
          </router-link>
        </div>
      </div>
      <textarea
        :value="showProcessed ? processedFormula : props.customField.formula"
        type="text"
        class="tw-w-full tw-rounded tw-border tw-border-dashed tw-border-gray-medium tw-p-2 tw-pr-8 tw-font-mono tw-text-xs tw-outline-primary"
        placeholder="Enter a formula"
        readonly
      />

      <div class="preview-section">
        <span class="preview-section__label"> Dependent Fields </span>
        <div class="tw-relative tw-text-xs">
          <div
            v-for="field in dependentFields"
            :key="field.name"
            class="tw-mb-1 tw-flex tw-items-center"
          >
            <div class="tw-mr-3 tw-min-w-0 tw-truncate" :title="field.label">
              <span class="tw-font-semibold">{{
                field.humanReadableName
              }}</span>
              <span
                class="tw-ml-1 tw-font-mono"
                :style="{
                  fontSize: '0.65rem',
                }"
                >({{ field.name }})</span
              >
            </div>
            <div class="tw-ml-auto tw-w-48 tw-shrink-0">
              <q-input
                :model-value="field.readableValue"
                readonly
                outlined
                dense
                class="input-xs"
              ></q-input>
            </div>
          </div>
        </div>
      </div>

      <label class="input-label tw-mt-6">Computed value</label>
      <calculated-custom-field
        :custom-field="props.customField"
        class="tw-mb-3"
        hide-label
        :value="(props.item.customFields[props.customField.id] as string)"
      ></calculated-custom-field>
    </div>
  </base-modal>
</template>

<script setup lang="ts">
import { PreparedWorkItem } from '@/models/item.models';
import { useCustomFields } from '@/utils/custom-fields';
import { computed, ref } from 'vue';
import CalculatedCustomField from './CalculatedCustomField.vue';
import { organizationData } from '@/store/helpers';
import { RouteNames } from '@/router';
import {
  getHumanReadableNameForVariable,
  replaceVariables,
} from '@/pages/settings/custom-fields/formula-input/utils';
import { get, uniq } from 'lodash-es';
import {
  Colors,
  CustomField,
  CustomFieldType,
  CustomFieldEntitySubType,
} from '@/api';
import BaseModal from '@/components/modal/templates/BaseModal.vue';
import { useKeyboardShortcuts } from '@/composables/modal/useKeyboardShortcuts';
import { returnFocusToTrigger } from '@/composables/modal';

export interface Props {
  item: PreparedWorkItem;
  customField: CustomField;
}
export interface Emits {
  (event: 'hide'): void;
}
const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { shortcuts, withShortcut } = useKeyboardShortcuts();

// Type for BaseModal exposed methods
interface BaseModalInstance {
  showPopup: (trigger?: HTMLElement) => void;
  closePopup: () => void;
  isVisible: boolean;
}

const baseModalRef = ref<BaseModalInstance>();
const triggerElement = ref<HTMLElement | null>(null);

function showDialog(trigger?: HTMLElement) {
  triggerElement.value = trigger || null;
  baseModalRef.value?.showPopup(trigger);
}

function closePopup() {
  baseModalRef.value?.closePopup();
  emit('hide');
}

function onHide() {
  emit('hide');
  returnFocusToTrigger(triggerElement);
}

defineExpose({
  showDialog,
  closePopup,
});

const { isAdmin, organization, orgMembershipCache } = organizationData();
const { customFields } = useCustomFields({ workTypeIds: ref([]) });

const dependentFields = computed(() => {
  const valueBag = {
    $: {
      input: {
        ...props.item,
        customFieldValues: props.item.customFields,
        assignee: props.item.assignee?.user,
        author: props.item.author?.user,
      },
    },
  };
  const ret: {
    name: string;
    humanReadableName: string;
    label: string;
    rawValue: string;
    readableValue: string;
    /** this can be shown in the code without violating the syntax. Based on readableValue */
    processedValue: string;
    isCustomField: boolean;
  }[] = [];
  const toProcessedString = (val: string | string[]) => {
    if (Array.isArray(val)) {
      return ['[', val.map((v) => `"${v}"`).join(', '), ']'].join('');
    }
    return `"${val}"`;
  };
  for (const varName of uniq(props.customField.variables || [])) {
    // name
    const humanReadableName = getHumanReadableNameForVariable(varName);

    // value
    const variable = organization.value?.variables?.[varName];
    const rawValue = (() =>
      variable ? get(valueBag, variable.referencePath) || '' : '')();

    // readable value
    let processedValue = '';
    const readableValue: string = (() => {
      if (!variable) {
        processedValue = toProcessedString('');
        return '<unknown>';
      }
      if (variable.entity === 'customField') {
        const cf = customFields.value?.find(
          (cf) => cf.id === variable.referenceId
        );
        if (!cf) {
          processedValue = toProcessedString('');
          return '<unknown>';
        }
        if (cf.type === CustomFieldType.CHOICE) {
          const valueArr = props.item.customFields[cf.id] as string[];
          const val =
            cf.options
              ?.filter((o) => valueArr.includes(o.id))
              .map((o) => o.label) ?? valueArr;

          processedValue = toProcessedString(cf.isMultiSelect ? val : val[0]);
          return cf.isMultiSelect ? val.join(', ') : val[0];
        } else if (
          cf.type === CustomFieldType.ENTITY &&
          cf.subType === CustomFieldEntitySubType.USER
        ) {
          const valueArr = props.item.customFields[cf.id] as string[];
          const val = valueArr.map((userId) => {
            const mem = orgMembershipCache.value?.find(
              (u) => u.userId === userId
            );
            return mem?.user.name ?? mem?.user.username ?? userId;
          });

          processedValue = toProcessedString(cf.isMultiSelect ? val : val[0]);
          return cf.isMultiSelect ? val.join(', ') : val[0];
        } else if (
          cf.type === CustomFieldType.NUMBER ||
          cf.type === CustomFieldType.BOOLEAN
        ) {
          const val = props.item.customFields[cf.id] as string;
          processedValue = val; // no need to wrap in quotes
          return val;
        } else {
          const val = props.item.customFields[cf.id] as string; // assume string type
          processedValue = toProcessedString(val);
          return val;
        }
      } else {
        const val = get(valueBag, variable.referencePath) || '';
        processedValue = toProcessedString(val);
        return val;
      }
    })();

    // processed value

    // is custom field
    const isCustomField = variable?.entity === 'customField';

    ret.push({
      name: varName,
      humanReadableName,
      label: `${humanReadableName} (${varName})`,
      rawValue,
      readableValue,
      processedValue,
      isCustomField,
    });
  }

  return ret;
});

const showProcessed = ref(false);
const processedFormula = computed(() => {
  if (!props.customField.formula) return '';

  const bag: Record<string, string> = {};
  for (const field of dependentFields.value) {
    bag[field.name] = field.processedValue;
  }
  return replaceVariables(props.customField.formula, bag);
});
</script>

<style scoped lang="scss">
.preview-section {
  @apply tw-border tw-rounded tw-p-3 tw-mt-5 tw-relative tw-border-gray-medium;
  min-height: 4rem;
}

.preview-section__label {
  @apply tw-font-bold tw-absolute tw--top-2 tw-left-2 tw-bg-white tw-px-1 tw-uppercase tw-text-gray-medium-dark;
  font-size: 0.65rem;
}
</style>
